import { useState, useCallback, useEffect, useRef } from 'react';
import { Check, FlaskConical, Droplets, PlayCircle, PauseCircle } from 'lucide-react';
import { useExperiment } from '@/context';
import { Burette, pHMeter } from '@/components/lab';
import ErlenmeyerFlask from '@/components/lab/ErlenmeyerFlask';
import MagneticStirrer from '@/components/lab/MagneticStirrer';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

interface DataPoint {
  volume: number;
  pH: number;
}

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Burette Preparation',
    content: 'The burette should be rinsed with NaOH solution before filling to prevent dilution. Read the meniscus at eye level for accurate volumes.'
  },
  2: {
    title: 'Initial pH',
    content: 'Aspirin (acetylsalicylic acid / ASA) is a weak acid. The initial pH reflects this weak acidity. Record the pH.',
    chemicalEquation: 'C9H8O4 + H₂O ⇌ C9H7O4- + H₃O⁺'
  },
  3: {
    title: 'Data Collection -- 5.0 mL Increments',
    content: 'Add NaOH solution from the burette in 5.0 mL increments for the first 20.0 mL. Record the titrant volume and pH after each addition. pH rises slowly followed by a sharp rise -- add very slowly during the sharp rise.'
  },
  4: {
    title: '1.0 mL Increments',
    content: 'Continue with successive additions of 1.0 mL base until within 2 mL of the end point. Record volume and pH after each addition.'
  },
  5: {
    title: 'Near Endpoint -- 0.5 mL Increments',
    content: 'When within 2 mL of the endpoint, add 0.5 mL increments of base. pH changes more rapidly in this region.'
  },
  6: {
    title: 'Endpoint Region -- 0.1 mL Increments',
    content: 'As the endpoint is approached, add 0.1 mL increments. When the pH no longer changes upon the addition of base, the titration is complete (to pH 12). Determine the end point from the inflection point of the curve.',
    chemicalEquation: 'CH₃CO₂C₆H₄COOH + NaOH → CH₃CO₂C₆H₄COONa + H₂O'
  }
};

// Simulate pH vs Volume for aspirin titration with NaOH
function simulatepH(volume: number): number {
  // Aspirin is a weak acid with pKa ~3.5
  // Initial pH ~3.0, equivalence point around 20-25 mL for typical sample
  const equivalenceVolume = 22.5; // mL

  if (volume < 2) {
    return 3.0 + volume * 0.15;
  } else if (volume < equivalenceVolume - 3) {
    // Buffer region - gradual rise
    const progress = (volume - 2) / (equivalenceVolume - 5);
    return 3.3 + progress * 2.5;
  } else if (volume < equivalenceVolume + 3) {
    // Rapid rise near equivalence point
    const distFromEq = volume - equivalenceVolume;
    const base = 8.5 + Math.atan(distFromEq * 2) * 2;
    return base;
  } else {
    // Excess base region - high pH
    const excess = volume - equivalenceVolume - 3;
    return Math.min(12.5, 10.5 + excess * 0.1);
  }
}

export default function Part3Titration() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();

  // State for info panel
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const partState = state?.experimentData?.titration || {
    burette: {
      initialVolume: 0,
      currentVolume: 50,
      NaOH_molarity: 0.100,
      tipOpen: false
    },
    titration: {
      dataPoints: [] as DataPoint[],
      currentVolume: 0,
      currentPH: 3.0,
      incrementSize: 1.0,
      nearEndpoint: false,
      phase: 'initial' // initial, buffer, nearEndpoint, pastEndpoint
    }
  };

  const [stirrerOn, setStirrerOn] = useState(true);
  const [titrating, setTitrating] = useState(false);
  const [currentPH, setCurrentPH] = useState(3.0);
  const [currentVolume, setCurrentVolume] = useState(0);
  const [autoMode, setAutoMode] = useState(false);
  const autoRef = useRef<NodeJS.Timeout | null>(null);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('titration', { ...partState, ...updates });
  }, [partState, updatePartData]);

  // Auto-titration mode
  useEffect(() => {
    if (autoMode && !isStepCompleted('titration-6')) {
      const increment = partState.titration.nearEndpoint ? 0.5 : 5.0;
      autoRef.current = setInterval(() => {
        setCurrentVolume(prev => {
          const newVolume = prev + increment;
          if (newVolume > 35) {
            setAutoMode(false);
            return prev;
          }
          return newVolume;
        });
      }, 800);
    }

    return () => {
      if (autoRef.current) {
        clearInterval(autoRef.current);
      }
    };
  }, [autoMode, partState.titration.nearEndpoint, isStepCompleted]);

  // Update pH and data points when volume changes
  useEffect(() => {
    const newPH = simulatepH(currentVolume);
    setCurrentPH(newPH);

    // Determine phase and near endpoint
    let phase = 'initial';
    let nearEndpoint = false;

    if (currentVolume > 5) phase = 'buffer';
    if (currentVolume > 18 && currentVolume < 27) {
      phase = 'nearEndpoint';
      nearEndpoint = true;
    }
    if (currentVolume > 27) phase = 'pastEndpoint';

    // Add data point
    const newDataPoints = [...partState.titration.dataPoints];
    const existingIndex = newDataPoints.findIndex(dp => Math.abs(dp.volume - currentVolume) < 0.05);
    if (existingIndex === -1 && currentVolume > 0) {
      newDataPoints.push({ volume: currentVolume, pH: newPH });
      newDataPoints.sort((a, b) => a.volume - b.volume);
    }

    updateState({
      titration: {
        ...partState.titration,
        dataPoints: newDataPoints,
        currentVolume,
        currentPH: newPH,
        nearEndpoint,
        phase
      },
      burette: {
        ...partState.burette,
        currentVolume: 50 - currentVolume
      }
    });
  }, [currentVolume]);

  const handleFillBurette = useCallback(() => {
    updateState({
      burette: { ...partState.burette, currentVolume: 50 }
    });
    completeStep('titration-1');
  }, [partState.burette, updateState, completeStep]);

  const handleRecordInitialPH = useCallback(() => {
    const initialPH = simulatepH(0);
    setCurrentPH(initialPH);
    const newDataPoints = [{ volume: 0, pH: initialPH }];
    updateState({
      titration: {
        ...partState.titration,
        dataPoints: newDataPoints,
        currentPH: initialPH,
        currentVolume: 0
      }
    });
    completeStep('titration-2');
  }, [partState.titration, updateState, completeStep]);

  const handleAddIncrement = useCallback((increment: number) => {
    const newVolume = currentVolume + increment;
    if (newVolume <= 50) {
      setCurrentVolume(newVolume);
    }
  }, [currentVolume]);

  const handleCompleteTitration = useCallback(() => {
    completeStep('titration-6');
    setAutoMode(false);
  }, [completeStep]);

  // Check for step completions based on phase
  useEffect(() => {
    const phase = partState.titration.phase;

    if (phase === 'buffer' && currentVolume > 10 && !isStepCompleted('titration-3')) {
      completeStep('titration-3');
    }
    if (phase === 'nearEndpoint' && !isStepCompleted('titration-4')) {
      completeStep('titration-4');
    }
    if (phase === 'nearEndpoint' && currentVolume > 20 && !isStepCompleted('titration-5')) {
      completeStep('titration-5');
    }
    if (phase === 'pastEndpoint' && currentPH > 12 && !isStepCompleted('titration-6')) {
      completeStep('titration-6');
    }
  }, [partState.titration.phase, currentVolume, currentPH, isStepCompleted, completeStep]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part III: Potentiometric Titration</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Fill Burette', hint: 'Fill with standardized NaOH' },
            { id: 2, title: 'Record Initial pH', hint: 'Record initial pH of aspirin solution' },
            { id: 3, title: 'Titrate: 5.0 mL Increments', hint: 'Add NaOH in 5.0 mL increments for first 20.0 mL' },
            { id: 4, title: 'Titrate: 1.0 mL Increments', hint: 'Switch to 1.0 mL until within 2 mL of endpoint' },
            { id: 5, title: 'Titrate: 0.5 mL Increments', hint: 'Switch to 0.5 mL increments near endpoint' },
            { id: 6, title: 'Titrate: 0.1 mL at Endpoint', hint: 'Continue until pH ~12' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`titration-${step.id}`) ? 'completed' : ''}`}
            >
              <div className={`step-number ${isStepCompleted(`titration-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`titration-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Data Points Table */}
        <div className="data-table">
          <h4>Titration Data ({partState.titration.dataPoints.length} points)</h4>
          <div className="data-scroll">
            <table>
              <thead>
                <tr>
                  <th>Vol (mL)</th>
                  <th>pH</th>
                </tr>
              </thead>
              <tbody>
                {partState.titration.dataPoints.slice(-8).map((dp: DataPoint, i: number) => (
                  <tr key={i}>
                    <td>{dp.volume.toFixed(2)}</td>
                    <td>{dp.pH.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Potentiometric Titration"
        completedCount={state?.completedSteps.filter(s => s.startsWith('titration-')).length || 0}
        totalSteps={6}
        equipment={['Burette (50 mL)', 'pH meter', 'Magnetic stirrer']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="titration-workspace">
          <div className="titration-layout">
            {/* Left: Titration Setup */}
            <div className="titration-setup">
              <div className="equipment-row">
                {/* Burette */}
                <div className="burette-station">
                  <Burette
                    volume={partState.burette.currentVolume}
                    maxVolume={50}
                    liquidColor="#7DD3FC"
                    stopcock={titrating ? 'open' : 'closed'}
                    onStopcock={() => setTitrating(!titrating)}
                  />
                  <span className="equipment-label">0.100 M NaOH</span>
                </div>

                {/* pH Meter and Flask */}
                <div className="flask-ph-assembly">
                  <pHMeter
                    reading={currentPH}
                    inSolution={true}
                  />
                  <ErlenmeyerFlask
                    size={250}
                    fillLevel={45}
                    fillColor="#FFFDE7"
                    hasStirBar={true}
                    stirring={stirrerOn}
                    label="Aspirin Solution"
                  />
                  <MagneticStirrer
                    speed={5}
                    isOn={stirrerOn}
                    hasFlask={true}
                    onClick={() => setStirrerOn(!stirrerOn)}
                  />
                </div>
              </div>

              {/* Titration Controls */}
              <div className="titration-controls">
                {!isStepCompleted('titration-1') && (
                  <button className="action-btn primary" onClick={handleFillBurette}>
                    <Droplets size={18} />
                    Fill Burette
                  </button>
                )}

                {isStepCompleted('titration-1') && !isStepCompleted('titration-2') && (
                  <button className="action-btn primary" onClick={handleRecordInitialPH}>
                    <Check size={18} />
                    Record Initial pH
                  </button>
                )}

                {isStepCompleted('titration-2') && !isStepCompleted('titration-6') && (
                  <div className="increment-controls">
                    <div className="increment-buttons">
                      <button
                        className={`increment-btn ${!partState.titration.nearEndpoint ? 'active' : ''}`}
                        onClick={() => handleAddIncrement(5.0)}
                        disabled={autoMode}
                      >
                        +5.0 mL
                      </button>
                      <button
                        className={`increment-btn`}
                        onClick={() => handleAddIncrement(1.0)}
                        disabled={autoMode}
                      >
                        +1.0 mL
                      </button>
                      <button
                        className={`increment-btn ${partState.titration.nearEndpoint ? 'active' : ''}`}
                        onClick={() => handleAddIncrement(0.5)}
                        disabled={autoMode}
                      >
                        +0.5 mL
                      </button>
                      <button
                        className={`increment-btn small`}
                        onClick={() => handleAddIncrement(0.1)}
                        disabled={autoMode}
                      >
                        +0.1 mL
                      </button>
                    </div>

                    <button
                      className={`auto-btn ${autoMode ? 'active' : ''}`}
                      onClick={() => setAutoMode(!autoMode)}
                    >
                      {autoMode ? <PauseCircle size={18} /> : <PlayCircle size={18} />}
                      {autoMode ? 'Pause Auto' : 'Auto Titrate'}
                    </button>
                  </div>
                )}

                {isStepCompleted('titration-6') && (
                  <div className="completion-message">
                    <Check size={24} />
                    <span>Titration complete! Proceed to analysis.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Real-time Graph */}
            <div className="graph-panel">
              <h4>pH vs Volume</h4>
              <div className="graph-container">
                <svg viewBox="0 0 300 200" className="titration-graph">
                  {/* Grid lines */}
                  {[0, 2, 4, 6, 8, 10, 12, 14].map((pH) => (
                    <line
                      key={`h-${pH}`}
                      x1="40"
                      y1={180 - pH * 12}
                      x2="290"
                      y2={180 - pH * 12}
                      stroke="var(--border)"
                      strokeWidth="0.5"
                    />
                  ))}
                  {[0, 10, 20, 30, 40].map((vol) => (
                    <line
                      key={`v-${vol}`}
                      x1={40 + vol * 6.25}
                      y1="12"
                      x2={40 + vol * 6.25}
                      y2="180"
                      stroke="var(--border)"
                      strokeWidth="0.5"
                    />
                  ))}

                  {/* Axes */}
                  <line x1="40" y1="180" x2="290" y2="180" stroke="var(--text-secondary)" strokeWidth="2" />
                  <line x1="40" y1="12" x2="40" y2="180" stroke="var(--text-secondary)" strokeWidth="2" />

                  {/* Axis labels */}
                  <text x="165" y="198" fontSize="10" fill="var(--text-secondary)" textAnchor="middle">Volume NaOH (mL)</text>
                  <text x="15" y="96" fontSize="10" fill="var(--text-secondary)" textAnchor="middle" transform="rotate(-90, 15, 96)">pH</text>

                  {/* pH scale labels */}
                  {[0, 4, 7, 10, 14].map((pH) => (
                    <text
                      key={`label-${pH}`}
                      x="35"
                      y={183 - pH * 12}
                      fontSize="8"
                      fill="var(--text-muted)"
                      textAnchor="end"
                    >
                      {pH}
                    </text>
                  ))}

                  {/* Volume scale labels */}
                  {[0, 10, 20, 30, 40].map((vol) => (
                    <text
                      key={`vlabel-${vol}`}
                      x={40 + vol * 6.25}
                      y="192"
                      fontSize="8"
                      fill="var(--text-muted)"
                      textAnchor="middle"
                    >
                      {vol}
                    </text>
                  ))}

                  {/* Data points and line */}
                  {partState.titration.dataPoints.length > 1 && (
                    <polyline
                      points={partState.titration.dataPoints.map((dp: DataPoint) =>
                        `${40 + dp.volume * 6.25},${180 - dp.pH * 12}`
                      ).join(' ')}
                      fill="none"
                      stroke="var(--primary)"
                      strokeWidth="2"
                    />
                  )}

                  {/* Data point markers */}
                  {partState.titration.dataPoints.map((dp: DataPoint, i: number) => (
                    <circle
                      key={i}
                      cx={40 + dp.volume * 6.25}
                      cy={180 - dp.pH * 12}
                      r="3"
                      fill="var(--primary)"
                    />
                  ))}

                  {/* Current point highlight */}
                  {currentVolume > 0 && (
                    <circle
                      cx={40 + currentVolume * 6.25}
                      cy={180 - currentPH * 12}
                      r="5"
                      fill="var(--secondary)"
                      stroke="white"
                      strokeWidth="2"
                    />
                  )}
                </svg>
              </div>

              {/* Current readings */}
              <div className="current-readings">
                <div className="reading">
                  <span className="label">Volume:</span>
                  <span className="value">{currentVolume.toFixed(2)} mL</span>
                </div>
                <div className="reading">
                  <span className="label">pH:</span>
                  <span className="value">{currentPH.toFixed(2)}</span>
                </div>
                <div className="reading">
                  <span className="label">Phase:</span>
                  <span className={`value phase-${partState.titration.phase}`}>
                    {partState.titration.phase === 'initial' ? 'Initial' :
                     partState.titration.phase === 'buffer' ? 'Buffer Region' :
                     partState.titration.phase === 'nearEndpoint' ? 'Near Endpoint' :
                     'Past Endpoint'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LabBench>

      <style>{`
        .titration-workspace {
          width: 100%;
          padding: 20px;
        }

        .titration-layout {
          display: flex;
          gap: 30px;
        }

        .titration-setup {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }

        .equipment-row {
          display: flex;
          gap: 40px;
          align-items: flex-end;
        }

        .burette-station, .flask-ph-assembly {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .titration-controls {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.primary {
          background: var(--primary);
          color: white;
        }

        .increment-controls {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .increment-buttons {
          display: flex;
          gap: 8px;
        }

        .increment-btn {
          padding: 10px 16px;
          background: var(--bg-elevated);
          border: 2px solid var(--border);
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .increment-btn:hover:not(:disabled) {
          border-color: var(--primary);
          color: var(--primary);
        }

        .increment-btn.active {
          border-color: var(--secondary);
          background: rgba(0, 168, 107, 0.1);
          color: var(--secondary);
        }

        .increment-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .auto-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: var(--bg-dark);
          border: 2px solid var(--border);
          border-radius: 6px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .auto-btn.active {
          background: var(--secondary);
          border-color: var(--secondary);
          color: white;
        }

        .completion-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 24px;
          background: rgba(0, 168, 107, 0.2);
          border-radius: 8px;
          color: var(--secondary);
          font-weight: 500;
        }

        .graph-panel {
          flex: 1;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
        }

        .graph-panel h4 {
          font-size: 14px;
          margin: 0 0 12px 0;
          color: var(--text-primary);
          text-align: center;
        }

        .graph-container {
          background: var(--bg-dark);
          border-radius: 8px;
          padding: 8px;
        }

        .titration-graph {
          width: 100%;
          height: auto;
        }

        .current-readings {
          display: flex;
          justify-content: space-around;
          margin-top: 16px;
          padding-top: 12px;
          border-top: 1px solid var(--border);
        }

        .reading {
          text-align: center;
        }

        .reading .label {
          display: block;
          font-size: 10px;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .reading .value {
          font-size: 16px;
          font-weight: 700;
          color: var(--text-primary);
          font-family: 'Courier New', monospace;
        }

        .reading .value.phase-nearEndpoint {
          color: var(--primary);
        }

        .reading .value.phase-pastEndpoint {
          color: var(--secondary);
        }

        .data-table {
          margin-top: 16px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .data-table h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 8px 0;
          text-transform: uppercase;
        }

        .data-scroll {
          max-height: 150px;
          overflow-y: auto;
        }

        .data-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        .data-table th, .data-table td {
          padding: 4px 8px;
          text-align: center;
          border-bottom: 1px solid var(--border);
        }

        .data-table th {
          color: var(--text-muted);
          font-weight: 600;
          position: sticky;
          top: 0;
          background: var(--bg-elevated);
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
